<template>
  <div
    :id="pageId"
    class="page-content"
    :class="pageId"
  >
    <modal
      v-model:show="modalShow"
      :header-classes="'scheduler-header'"
      :body-classes="'scheduler-body'"
      :footer-classes="'scheduler-footer'"
    >
      <div class="col-md-12">
        <span
          class="close"
          @click="onCloseModal"
        >
          <i class="fa fa-close" />
        </span>
        <div class="row order-details">
          <div
            v-if="estimate"
            class="col-12"
          >
            <div>
              <div>
                <div class="item-title h6">
                  <strong>Restaurant:</strong>
                  {{ storeTitle }}
                </div>
                <div>
                  <strong>Client:</strong>
                  {{ customerFullName }} ({{ customerPhoneNumber }})
                </div>
                <div>
                  <strong>Address:</strong>
                  {{ estimate.address && estimate.address.formattedAddress }}
                </div>
                <div>
                  <strong>Address 2:</strong>
                  {{ estimate.address && estimate.address.streetAddress2 }}
                </div>
              </div>
              <div v-if="scheduledOrderId">
                <strong>Order ID:</strong>
                {{ scheduledOrderId }}
              </div>
              <div v-if="estimate.timeEstimate">
                <strong>Estimate:</strong>
                {{ estimate.timeEstimate.min }} - {{ estimate.timeEstimate.max }} min
              </div>
              <div v-if="estimate.orderOptions">
                <strong>Delivery Instructions:</strong>
                {{ estimate.orderOptions.deliveryInstructions }}
              </div>
            </div>
            <div class="row border-top order-items">
              <div class="col-8 order-details-info">
                <div>
                  <h6>Items</h6>
                  <ol class="items-list">
                    <li
                      v-for="(item, index) in estimate.items"
                      :key="index"
                    >
                      {{ item.item.title }}(Quantity: {{ item.item.quantity || 1 }}) - ${{ (item.item.price / 100).toFixed(2) }}
                      <order-options :options="getOptions(item.options)" />
                    </li>
                  </ol>
                </div>
                <br>
              </div>
              <div class="col-4">
                <h6>Receipt (${{ (estimate.total / 100).toFixed(2) }})</h6>
                <div
                  v-for="(charge, index) in estimate.charges"
                  :key="index"
                >
                  <span class="charge-title">{{ charge.title }}: </span>${{ (charge.value / 100).toFixed(2) }}
                </div>
                <br>
              </div>
            </div>
            <p-button
              v-if="scheduledOrderId"
              type="info"
              round
              @click="$emit('edit-order', scheduledOrderId)"
            >
              Edit Order
            </p-button>
          </div>
        </div>
        <div>
          <customized-form @submit="onSubmitForm">
            <div class="schedule-details border-top">
              <div class="row schedule-details__type-label">
                <label class="col-sm-2 control-label">Delivery Type</label>
                <div class="col-sm-8">
                  <p-button
                    type="purple"
                    round
                    :pressed="schedule.isRepeated === false"
                    @click="schedule.isRepeated = false"
                  >
                    <span
                      v-if="!schedule.isRepeated"
                      class="btn-label"
                    >
                      <i class="fa fa-check" />
                    </span>
                    SINGLE DELIVERY
                  </p-button>

                  <p-button
                    type="purple"
                    round
                    :pressed="schedule.isRepeated === true"
                    @click="schedule.isRepeated = true"
                  >
                    <span
                      v-if="schedule.isRepeated"
                      class="btn-label"
                    >
                      <i class="fa fa-check" />
                    </span>
                    REPEATED DELIVERY
                    <i class="fa fa-repeat" />
                  </p-button>
                </div>
              </div>

              <!-- Single Schedule -->
              <div
                v-if="!schedule.isRepeated"
                class="single"
              >
                <div class="row">
                  <label class="col-sm-2 control-label pt-10"> Delivery Date* </label>
                  <div class="col-sm-4">
                    <date-picker
                      v-model="schedule.deliveryDate"
                      required
                    />
                  </div>
                </div>
                <div class="row">
                  <label class="col-sm-2 control-label pt-10"> Delivery Time* </label>

                  <time-picker
                    v-model="schedule.deliveryTime"
                    class="col-sm-4"
                    required
                    :clearable="false"
                  >
                    <template #comment>
                      <span
                        v-if="isShowWarningMessage"
                        class="help-block warning-message"
                      >
                        Please inform the rider that it may be hard to get a driver at this scheduled time.
                      </span>
                    </template>
                  </time-picker>
                </div>
              </div>

              <!-- Repeated Schedule -->
              <div
                v-if="schedule.isRepeated"
                class="repeat"
              >
                <div>
                  <div class="row">
                    <label class="col-sm-2 control-label pt-10"> Delivery Time* </label>
                    <time-picker
                      v-model="schedule.deliveryTime"
                      class="col-sm-4"
                      required
                      :clearable="false"
                    >
                      <template #comment>
                        <span
                          v-if="isShowWarningMessage"
                          class="help-block warning-message"
                        >
                          Please inform customer that it may be hard to get a driver at this scheduled time.
                        </span>
                      </template>
                    </time-picker>
                  </div>
                </div>
                <div class="row align-items-center">
                  <label class="col-sm-2 control-label">Repeat</label>
                  <div class="col-sm-4">
                    <el-select
                      v-model="schedule.repeatEveryXWeek"
                      class="select-primary"
                      size="large"
                      placeholder="Select One"
                    >
                      <el-option
                        v-for="(repeat, index) in repeatWeeks"
                        :key="index"
                        :value="repeat.id"
                        :label="repeat.label"
                      >
                        {{ repeat.label }}
                      </el-option>
                    </el-select>
                  </div>
                </div>
                <div class="row repeat-on">
                  <label class="col-sm-2 control-label">Repeat on</label>
                  <div class="col-sm-8">
                    <div class="row">
                      <div class="col-sm-3 col-6 checkbox-container">
                        <el-checkbox v-model="schedule.day0">
                          Sunday
                        </el-checkbox>
                      </div>

                      <div class="col-sm-3 col-6 checkbox-container">
                        <el-checkbox v-model="schedule.day1">
                          Monday
                        </el-checkbox>
                      </div>

                      <div class="col-sm-3 col-6 checkbox-container">
                        <el-checkbox v-model="schedule.day2">
                          Tuesday
                        </el-checkbox>
                      </div>

                      <div class="col-sm-3 col-6 checkbox-container">
                        <el-checkbox v-model="schedule.day3">
                          Wednesday
                        </el-checkbox>
                      </div>

                      <div class="col-sm-3 col-6 checkbox-container">
                        <el-checkbox v-model="schedule.day4">
                          Thursday
                        </el-checkbox>
                      </div>

                      <div class="col-sm-3 col-6 checkbox-container">
                        <el-checkbox v-model="schedule.day5">
                          Friday
                        </el-checkbox>
                      </div>

                      <div class="col-sm-3 col-6 checkbox-container">
                        <el-checkbox v-model="schedule.day6">
                          Saturday
                        </el-checkbox>
                      </div>

                      <p
                        v-if="isRepeatedRequired"
                        class="error-message col-12"
                      >
                        A multi-delivery requires at least one of the days (Mon-Sun) to be checked
                      </p>
                    </div>
                  </div>
                </div>
                <div class="row align-items-center">
                  <label class="col-sm-2 control-label">Starts on*</label>
                  <div class="col-sm-2">
                    <date-picker
                      v-model="schedule.deliveryDate"
                      required
                    />
                  </div>
                </div>
                <div class="row">
                  <label class="col-sm-2 control-label"> Ends on </label>
                  <div class="col-sm-8">
                    <div class="pb-10">
                      <el-radio
                        v-model="schedule.endOnType"
                        label="never"
                      >
                        Never
                      </el-radio>
                    </div>

                    <div class="pb-10">
                      <el-radio
                        v-model="schedule.endOnType"
                        label="after"
                        class="flex-radio"
                      >
                        After
                        <input
                          v-model.number="schedule.endAfterXTimes"
                          type="number"
                          class="form-control occurences-num ml-5 mr-5 pl-10 pr-10"
                          :min="schedule.endOnType === 'after' ? 1 : 0"
                          autocomplete="nope"
                        >
                        occurences
                      </el-radio>
                    </div>

                    <div class="pb-10">
                      <el-radio
                        v-model="schedule.endOnType"
                        label="on"
                        class="flex-radio"
                      >
                        On
                        <date-picker
                          v-model="schedule.endOn"
                          class="pl-10"
                          :required="isEndOnDateRequired"
                        />
                      </el-radio>
                    </div>
                  </div>
                </div>
                <div class="row align-items-center">
                  <label class="col-sm-2 control-label">Skip Dates</label>
                  <div class="col-sm-2">
                    <date-picker
                      v-model="schedule.skipDates"
                      type="dates"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div class="form-submit-button-group">
                <p-button
                  v-if="!initialSchedule"
                  type="purple"
                  round
                  :disabled="isRepeatedRequired"
                  native-type="submit"
                >
                  Schedule
                </p-button>

                <template v-if="initialSchedule && status === 'scheduled' && !initialSchedule.completedAt && !initialSchedule.canceledAt">
                  <p-button
                    type="info"
                    round
                    :disabled="isRepeatedRequired"
                    native-type="submit"
                  >
                    Update Schedule
                  </p-button>
                  <p-button
                    type="info"
                    round
                    @click="$emit('cancel-delivery', scheduledOrderId)"
                  >
                    Cancel Schedule
                  </p-button>
                </template>

                <p-button
                  type="yellow"
                  round
                  @click="onCloseModal"
                >
                  Cancel
                </p-button>
              </div>
            </div>
          </customized-form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script lang="ts">
import { ref, Ref, watch, computed, WritableComputedRef } from 'vue';
import Moment from 'moment-timezone';
import _ from 'lodash';

import util from '@/Utils/util';
import { useCustomer } from '@/use/customer';
import { repeatWeeks } from '@/constants/ride';

import Modal from '@/components/UIComponents/Modal.vue';
import OrderOptions from '@/components/UIComponents/OrderingForms/OrderOptions.vue';

const pageId = 'delivery-scheduler';

export default {
  name: 'DeliveryScheduler',

  components: {
    Modal,
    OrderOptions,
  },

  props: {
    show: {
      type: Boolean,
      default: false,
    },

    estimate: {
      type: Object,
      default: () => null,
    },

    storeTitle: {
      type: String,
      default: '',
    },

    initialSchedule: {
      type: Object,
      default: () => null,
    },

    scheduledOrderId: {
      type: String,
      default: '',
    },

    status: {
      type: String,
      default: '',
    },
  },

  emits: ['update-delivery', 'schedule-delivery', 'update:show', 'edit-order', 'cancel-delivery', 'update:show', 'edit-order'],

  setup: (props: any, context: any) => {
    const { emit } = context;

    const { customerFullName, customerPhoneNumber } = useCustomer();

    const schedule: Ref<any> = ref({
      isRepeated: false,
      deliveryDate: null,
      deliveryTime: '',
      repeatEveryXWeek: 1,
      day0: false,
      day1: false,
      day2: false,
      day3: false,
      day4: false,
      day5: false,
      day6: false,
      endOn: null,
      endOnType: 'never', // never, after, on
      endAfterXTimes: 0,
      minutesToAutoRetryRequest: null,
      minutesBeforeCustomReminder: null,
    });
    const isShowWarningMessage = ref(false);

    const modalShow: WritableComputedRef<boolean> = computed({
      get() {
        return props.show;
      },
      set(value) {
        emit('update:show', value);
      },
    });

    const isEndOnDateRequired = computed(() => schedule.value.endOnType === 'on' && schedule.value.isRepeated);

    const isRepeatedRequired = computed(
      () =>
        schedule.value.isRepeated &&
        !schedule.value.day0 &&
        !schedule.value.day1 &&
        !schedule.value.day2 &&
        !schedule.value.day3 &&
        !schedule.value.day4 &&
        !schedule.value.day5 &&
        !schedule.value.day6,
    );

    const onSubmitForm = () => {
      if (props.initialSchedule) {
        emit('update-delivery', formatSchedule());
      } else {
        emit('schedule-delivery', formatSchedule());
      }
    };

    const initSchedule = () => {
      schedule.value = _.cloneDeep(props.initialSchedule);
      schedule.value.deliveryTime = Moment(props.initialSchedule.deliveryTime, 'hh:mm A').toString();
    };

    const formatSchedule = () => {
      const customSchedule = _.cloneDeep(schedule.value);

      if (customSchedule.deliveryDate) {
        customSchedule.deliveryDate = util.formatDateTime(customSchedule.deliveryDate, 'YYYY-MM-DD');
      }

      if (customSchedule.deliveryTime) {
        customSchedule.deliveryTime = util.formatDateTime(customSchedule.deliveryTime, 'hh:mm A');
      }

      if (customSchedule.isRepeated) {
        customSchedule.skipDates = !customSchedule.skipDates
          ? null
          : customSchedule.skipDates.map((date: any) => util.formatDateTime(date, 'YYYY-MM-DD'));
      } else {
        customSchedule.skipDates = null;
      }

      delete customSchedule.timeZone; // timeZone is set in the backend

      return customSchedule;
    };

    const getOptions = (options: any) => {
      if (options) {
        return util.optionsFormatter(options);
      }

      return [];
    };

    const onCloseModal = () => {
      emit('update:show', false);
    };

    const resetForm = () => {
      schedule.value = {
        isRepeated: false,
        deliveryDate: null,
        deliveryTime: null,
        repeatEveryXWeek: 1,
        day0: false,
        day1: false,
        day2: false,
        day3: false,
        day4: false,
        day5: false,
        day6: false,
        endOn: null,
        endOnType: 'never',
        endAfterXTimes: 0,
        minutesToAutoRetryRequest: null,
        minutesBeforeCustomReminder: null,
      };
    };

    watch(
      () => props.initialSchedule,
      (value: any) => {
        resetForm();

        if (value) {
          initSchedule();
        }
      },
      {
        immediate: true,
      },
    );

    watch(
      () => schedule.value.endOnType,
      (value: string) => {
        if (value === 'after') {
          schedule.value.endAfterXTimes = schedule.value.endAfterXTimes || 0;
        }
      },
    );

    watch(
      () => schedule.value.deliveryTime,
      (value: string) => {
        if (!value) {
          return;
        }

        isShowWarningMessage.value = false;

        const currentTime = util.formatDateTime(value, 'HH:mm') as string;
        if (currentTime.localeCompare('00:00') >= 0 && currentTime.localeCompare('05:00') <= 0) {
          isShowWarningMessage.value = true;
        }
      },
    );

    return {
      pageId,
      repeatWeeks,
      customerFullName,
      customerPhoneNumber,
      schedule,
      isEndOnDateRequired,
      isShowWarningMessage,
      isRepeatedRequired,
      modalShow,

      onSubmitForm,
      initSchedule,
      formatSchedule,
      getOptions,
      onCloseModal,
      resetForm,
    };
  },
};
</script>

<style lang="scss">
.delivery-scheduler.page-content {
  .btn {
    padding: 9px 15px !important;
  }
  .mx-datepicker {
    .mx-input-append {
      i {
        font-size: 20px;
      }
    }
    .mx-calendar-header {
      display: none;
    }
    .mx-calendar-content {
      width: 150px;
    }
    .mx-panel-time {
      .mx-time-list {
        width: 50% !important;
        &:last-child {
          display: none;
        }
      }
    }
  }
  .el-date-editor {
    width: 200px !important;
  }
  .occurences-num {
    width: 80px;
    margin: 0 5px;
    padding: 0 10px;
    @media (max-width: 576px) {
      margin: 0 10px;
    }
  }
  .flex-radio {
    .el-radio__label {
      display: flex;
      align-items: center;
      width: fit-content;
      span.form-check-sign::before,
      span.form-check-sign::after {
        top: 5px;
      }
    }
  }
  .modal {
    .modal-dialog {
      min-width: 700px;
      .modal-content {
        .scheduler-header,
        .scheduler-footer {
          display: none !important;
        }
        .scheduler-body {
          padding: 20px;
          overflow: scroll;
          max-height: 600px;
          .col-md-12 {
            padding: 0;
          }
          .order-details {
            .col-12 {
              padding: 0;
            }
          }
          .order-items {
            padding-top: 20px;
            margin: 20px 0 15px 0;
          }
          .schedule-details {
            padding: 20px;
            &__type-label {
              align-items: center;
            }
            .control-label {
              text-align: left;
            }
            .form-check {
              min-width: 130px;
            }
          }
        }
      }
    }
  }
  .error-message {
    color: red;
    text-align: left;
  }
}
</style>
